// components/NotificationManager.jsx
import React, { useEffect, useState } from 'react';
import { Badge, Button, Modal, ListGroup, Alert } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { useExhibition } from '../context/ExhibitionContext';
import { externalNotificationService } from '../utils/externalNotificationService';

const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();
  const { selectedExhibition } = useExhibition();

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        setIsLoading(true);
        await externalNotificationService.initialize({
          websocketUrl: process.env.REACT_APP_WEBSOCKET_URL
        });

        // Subscribe to current exhibition if selected
        if (selectedExhibition?.value) {
          await externalNotificationService.subscribeToExhibition(selectedExhibition.value);
        }

        // Load stored notifications
        loadNotifications();

        // Listen for new notifications
        externalNotificationService.addEventListener('notification', handleNewNotification);

      } catch (error) {
        console.error('Failed to initialize notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeNotifications();

    // Cleanup on unmount
    return () => {
      externalNotificationService.removeEventListener('notification', handleNewNotification);
    };
  }, [selectedExhibition]);

  // Load notifications from storage
  const loadNotifications = () => {
    const stored = externalNotificationService.getStoredNotifications();
    const sortedNotifications = stored.sort((a, b) => 
      new Date(b.receivedAt) - new Date(a.receivedAt)
    );
    
    setNotifications(sortedNotifications);
    setUnreadCount(sortedNotifications.filter(n => !n.read).length);
  };

  // Handle new notification
  const handleNewNotification = (data) => {
    console.log('New notification received:', data);
    loadNotifications(); // Refresh the list
  };

  // Mark notification as read
  const markAsRead = (notificationId) => {
    externalNotificationService.markNotificationAsRead(notificationId);
    loadNotifications();
  };

  // Mark all as read
  const markAllAsRead = () => {
    notifications.forEach(notification => {
      if (!notification.read) {
        externalNotificationService.markNotificationAsRead(notification.id);
      }
    });
    loadNotifications();
  };

  // Clear all notifications
  const clearAll = () => {
    externalNotificationService.clearAllNotifications();
    loadNotifications();
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return language === 'en' ? 'Just now' : 'Μόλις τώρα';
    if (diffInMinutes < 60) return `${diffInMinutes}${language === 'en' ? 'm ago' : 'λ πριν'}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}${language === 'en' ? 'h ago' : 'ώ πριν'}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}${language === 'en' ? 'd ago' : 'η πριν'}`;
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    const icons = {
      'event_reminder': 'bi-calendar-event',
      'exhibitor_update': 'bi-building',
      'schedule_change': 'bi-clock',
      'general_announcement': 'bi-megaphone',
      'registration_confirmation': 'bi-check-circle',
      'qr_scan_success': 'bi-qr-code',
      'system_maintenance': 'bi-gear'
    };
    return icons[type] || 'bi-bell';
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Handle navigation
    if (notification.data?.url) {
      if (notification.data.url.startsWith('/')) {
        window.location.href = notification.data.url;
      } else {
        window.open(notification.data.url, '_blank');
      }
      setShowModal(false);
    }
  };

  return (
    <>
      {/* Notification Bell Button */}
      <div className="notification-manager position-relative">
            <Button
      variant="link"
      className="text-dark p-2 position-relative d-flex align-items-center justify-content-center"
      onClick={() => setShowModal(true)}
      style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.05)'}
      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      title={language === 'en' ? 'Notifications' : 'Ειδοποιήσεις'}
    >
      <i className="bi bi-bell" style={{ fontSize: '1.25rem' }}></i>
      {unreadCount > 0 && (
        <Badge 
          bg="danger" 
          pill
          className="position-absolute"
          style={{
            top: '6px',
            right: '6px',
            fontSize: '0.65rem',
            minWidth: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}
    </Button>
      </div>

      {/* Notification Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-bell me-2"></i>
            {language === 'en' ? 'Notifications' : 'Ειδοποιήσεις'}
            {unreadCount > 0 && (
              <Badge bg="primary" className="ms-2">
                {unreadCount}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">{language === 'en' ? 'Loading notifications...' : 'Φόρτωση ειδοποιήσεων...'}</p>
            </div>
          ) : notifications.length === 0 ? (
            <Alert variant="info" className="text-center">
              <i className="bi bi-bell-slash fs-1 d-block mb-3"></i>
              <h5>{language === 'en' ? 'No notifications yet' : 'Δεν υπάρχουν ειδοποιήσεις'}</h5>
              <p className="mb-0">
                {language === 'en' 
                  ? 'You will receive notifications about events, updates, and important information here.'
                  : 'Θα λαμβάνετε ειδοποιήσεις για εκδηλώσεις, ενημερώσεις και σημαντικές πληροφορίες εδώ.'}
              </p>
            </Alert>
          ) : (
            <>
              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-muted">
                  {notifications.length} {language === 'en' ? 'notifications' : 'ειδοποιήσεις'}
                </small>
                <div>
                  {unreadCount > 0 && (
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={markAllAsRead}
                      className="me-2"
                    >
                      <i className="bi bi-check-all me-1"></i>
                      {language === 'en' ? 'Mark all read' : 'Όλα ως αναγνωσμένα'}
                    </Button>
                  )}
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={clearAll}
                  >
                    <i className="bi bi-trash me-1"></i>
                    {language === 'en' ? 'Clear all' : 'Καθαρισμός'}
                  </Button>
                </div>
              </div>

              {/* Notifications List */}
              <ListGroup variant="flush">
                {notifications.map((notification) => (
                  <ListGroup.Item
                    key={notification.id}
                    className={`notification-item ${!notification.read ? 'bg-light border-start border-primary border-3' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="d-flex align-items-start">
                      {/* Icon */}
                      <div className="me-3 mt-1">
                        <i 
                          className={`${getNotificationIcon(notification.type)} fs-4`}
                          style={{ 
                            color: !notification.read ? '#0ea5e9' : '#6c757d' 
                          }}
                        ></i>
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 
                            className={`mb-1 ${!notification.read ? 'fw-bold' : ''}`}
                            style={{ fontSize: '0.95rem' }}
                          >
                            {notification.title}
                          </h6>
                          <small className="text-muted flex-shrink-0 ms-2">
                            {formatTimestamp(notification.receivedAt)}
                          </small>
                        </div>
                        
                        <p 
                          className="mb-1 text-muted"
                          style={{ 
                            fontSize: '0.875rem',
                            lineHeight: '1.4'
                          }}
                        >
                          {notification.body}
                        </p>

                        {/* Priority indicator */}
                        {notification.priority === 'high' && (
                          <div className="mt-2">
                            <Badge bg="danger" className="me-2">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              {language === 'en' ? 'High Priority' : 'Υψηλή Προτεραιότητα'}
                            </Badge>
                          </div>
                        )}

                        {/* Unread indicator */}
                        {!notification.read && (
                          <div className="mt-2">
                            <Badge bg="primary">
                              {language === 'en' ? 'New' : 'Νέο'}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {language === 'en' ? 'Close' : 'Κλείσιμο'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Styles */}
      <style jsx>{`
        .notification-item:hover {
          background-color: #f8f9fa !important;
        }

        .notification-manager .btn:focus {
          box-shadow: none;
        }

        .notification-manager .position-absolute {
          font-size: 0.65rem;
          padding: 0.25em 0.4em;
        }

        @media (max-width: 768px) {
          .modal-lg {
            max-width: 95%;
          }
        }
      `}</style>
    </>
  );
};

// Hook for using notifications in components
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshNotifications = () => {
    const stored = externalNotificationService.getStoredNotifications();
    setNotifications(stored);
    setUnreadCount(stored.filter(n => !n.read).length);
  };

  useEffect(() => {
    refreshNotifications();
    
    const handleNotification = () => {
      refreshNotifications();
    };

    externalNotificationService.addEventListener('notification', handleNotification);
    
    return () => {
      externalNotificationService.removeEventListener('notification', handleNotification);
    };
  }, []);

  return {
    notifications,
    unreadCount,
    markAsRead: (id) => {
      externalNotificationService.markNotificationAsRead(id);
      refreshNotifications();
    },
    clearAll: () => {
      externalNotificationService.clearAllNotifications();
      refreshNotifications();
    },
    sendNotification: externalNotificationService.sendExternalNotification.bind(externalNotificationService),
    scheduleEventReminder: externalNotificationService.scheduleEventReminder.bind(externalNotificationService)
  };
};

// Test notification component (for development)
export const NotificationTester = () => {
  const { language } = useLanguage();
  const { selectedExhibition } = useExhibition();

  const sendTestNotification = async (type) => {
    try {
      const testNotifications = {
        event_reminder: {
          title: language === 'en' ? 'Event Reminder' : 'Υπενθύμιση Εκδήλωσης',
          body: language === 'en' 
            ? 'Your event "Innovation Summit" starts in 30 minutes'
            : 'Η εκδήλωση "Innovation Summit" ξεκινά σε 30 λεπτά',
          data: { url: '/events' },
          priority: 'high'
        },
        exhibitor_update: {
          title: language === 'en' ? 'Exhibitor Update' : 'Ενημέρωση Εκθέτη',
          body: language === 'en'
            ? 'TechCorp has added new products to their showcase'
            : 'Η TechCorp πρόσθεσε νέα προϊόντα στην έκθεσή της',
          data: { url: '/exhibitors' }
        },
        general_announcement: {
          title: language === 'en' ? 'Important Announcement' : 'Σημαντική Ανακοίνωση',
          body: language === 'en'
            ? 'Exhibition hours have been extended until 9 PM today'
            : 'Οι ώρες της έκθεσης έχουν παραταθεί μέχρι τις 9 μ.μ. σήμερα',
          data: { url: '/info' }
        }
      };

      await externalNotificationService.sendExternalNotification(
        type,
        testNotifications[type]
      );
      
    } catch (error) {
      console.error('Failed to send test notification:', error);
    }
  };

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className="position-fixed bottom-0 start-0 p-3" style={{ zIndex: 9999 }}>
      <div className="card">
        <div className="card-header">
          <small>Notification Tester</small>
        </div>
        <div className="card-body p-2">
          <div className="d-grid gap-1">
            <Button size="sm" onClick={() => sendTestNotification('event_reminder')}>
              Event Reminder
            </Button>
            <Button size="sm" onClick={() => sendTestNotification('exhibitor_update')}>
              Exhibitor Update
            </Button>
            <Button size="sm" onClick={() => sendTestNotification('general_announcement')}>
              Announcement
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;